import React, { useEffect, useState } from 'react';
import { LaptopIcon } from 'lucide-react';
import { Student } from '../../types';
import { BottomSheet, PanelRow, SidePanel } from '../ui/SidePanel';
import { UnderlineTabs } from '../ui/Tabs';
import { Field, NativeSelect, TextInput } from '../ui/Field';
import { deviceMakeOptions, schoolName } from '../../data/district';
import { devicesForStudent } from '../../data/devices';
import { useToast } from '../../contexts/ToastContext';
type Sheet = 'none' | 'student' | 'device';
const tabs = [{
  value: 'plans',
  label: 'Plans'
}, {
  value: 'devices',
  label: 'Covered Devices'
}];
export function StudentPanel({
  student,
  onClose
}: {
  student: Student | null;
  onClose: () => void;
}) {
  const {
    showToast
  } = useToast();
  const [tab, setTab] = useState('plans');
  const [sheet, setSheet] = useState<Sheet>('none');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [studentId, setStudentId] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [assetTag, setAssetTag] = useState('');
  useEffect(() => {
    if (student) {
      const [first, ...rest] = student.name.split(' ');
      setFirstName(first);
      setLastName(rest.join(' '));
      setEmail(student.email);
      setStudentId(student.studentId);
      setTab('plans');
      setSheet('none');
      const device = devicesForStudent(student.id)[0];
      setMake(device?.make ?? '');
      setModel(device?.model ?? '');
      setSerial(device?.serialNumber ?? '');
      setAssetTag(device?.assetTagId ?? '');
    }
  }, [student]);
  if (!student) return null;
  const studentDevices = devicesForStudent(student.id);
  const firstDevice = studentDevices[0];
  const planDeviceName = firstDevice ? `${firstDevice.make} ${firstDevice.model.split(' ')[0]}` : 'Covered device';
  return <SidePanel open title={student.name} onClose={onClose} action={<button type="button" onClick={() => setSheet('student')} className="h-8 shrink-0 rounded border border-ink-300 px-3 text-[12.5px] font-medium text-ink-700 hover:bg-ink-50" data-id="element-250" data-mp="3rx4g14mxm1jp">
          Edit Info
        </button>} sheet={sheet === 'student' ? <BottomSheet title="Edit Student Information" onCancel={() => setSheet('none')} onSave={() => {
    setSheet('none');
    showToast('Student Updated', 'Student information has been saved successfully');
  }} data-id="element-251" data-mp="340gx185q6h5w">
            <Field label="First Name" htmlFor="first-name" data-id="element-252" data-mp="3hzqbo3ea937r">
              <TextInput id="first-name" value={firstName} onChange={setFirstName} placeholder="Enter First Name" data-id="element-253" data-mp="3hx3urd5i5bcv" />
            </Field>
            <Field label="Last Name" htmlFor="last-name" data-id="element-254" data-mp="x8g3u56wy6jb">
              <TextInput id="last-name" value={lastName} onChange={setLastName} placeholder="Enter Last Name" data-id="element-255" data-mp="xb2am2lzci0h" />
            </Field>
            <Field label="Student Email" htmlFor="student-email" data-id="element-256" data-mp="12dkz1egwny09">
              <TextInput id="student-email" value={email} onChange={setEmail} placeholder="Enter Email" data-id="element-257" data-mp="12gfhx1k9tark" />
            </Field>
            <Field label="Student ID" htmlFor="student-id" data-id="element-258" data-mp="tm12myzqq08g">
              <TextInput id="student-id" value={studentId} onChange={setStudentId} placeholder="Enter Student ID" data-id="element-259" data-mp="tlj2vsdvd3vs" />
            </Field>
          </BottomSheet> : sheet === 'device' ? <BottomSheet title="Edit Device Information" onCancel={() => setSheet('none')} onSave={() => {
    setSheet('none');
    showToast('Device Updated', 'Device information has been saved successfully');
  }} data-id="element-260" data-mp="jma8iyf3dn8w">
            <Field label="Device Make" htmlFor="panel-device-make" data-id="element-261" data-mp="29200nhfn8fuz">
              <NativeSelect id="panel-device-make" value={make} onChange={setMake} options={deviceMakeOptions} placeholder="Enter Device Make" data-id="element-262" data-mp="250odrjo5m8qh" />
            </Field>
            <Field label="Device Model" htmlFor="panel-device-model" data-id="element-263" data-mp="2h0v5qtbdg9up">
              <TextInput id="panel-device-model" value={model} onChange={setModel} placeholder="Enter Device Model" data-id="element-264" data-mp="232wwu3ftl5ml" />
            </Field>
            <Field label="Serial Number" htmlFor="panel-device-serial" data-id="element-265" data-mp="1tdn2egxi7avb">
              <TextInput id="panel-device-serial" value={serial} onChange={setSerial} placeholder="Enter Serial Number" data-id="element-266" data-mp="1tbk1h1utbxbt" />
            </Field>
            <Field label="Asset Tag ID" htmlFor="panel-device-asset" data-id="element-267" data-mp="1jfz5bkcfdr9z">
              <TextInput id="panel-device-asset" value={assetTag} onChange={setAssetTag} placeholder="Enter Asset Tag ID" data-id="element-268" data-mp="1jdmf6jwikalu" />
            </Field>
          </BottomSheet> : null} data-id="element-249" data-mp="3mz2zaqczaebe">
      <dl className="pt-1" data-id="element-269" data-mp="1agvj47hyovr9">
        <PanelRow label="School" value={schoolName(student.schoolSlug)} data-id="element-270" data-mp="1fdly0ri15lb3" />
        <PanelRow label="Student ID" value={student.studentId} data-id="element-271" data-mp="1ffomv87g58e8" />
        <PanelRow label="Guardian Name" value={student.guardianName} data-id="element-272" data-mp="vsltcs8fhlst" />
        <PanelRow label="Guardian Email" value={student.guardianEmail} data-id="element-273" data-mp="qtsf2xlbhqrl" />
      </dl>

      <div className="px-5 pt-4" data-id="element-274" data-mp="14sie4irp60il">
        <UnderlineTabs label="Student details" options={tabs} value={tab} onChange={setTab} data-id="element-275" data-mp="14tjoel0w2iz9" />
      </div>

      <div className="space-y-3 px-5 py-4" data-id="element-276" data-mp="10plnc4s06y3k">
        {tab === 'plans' && <div className="rounded-lg border border-ink-200 px-4 py-3" data-id="element-277" data-mp="1eq6a1c11he2c">
            <p className="text-[13px] font-bold text-ink-900" data-id="element-278" data-mp="19s4o8jc8b0n6">{planDeviceName}</p>
            <p className="mt-1 text-[12.5px] text-ink-500" data-id="element-279" data-mp="19q0li4q2g7oh">
              Purchase Date: {student.planPurchaseDate} • Contract ID: {student.contractId}
            </p>
          </div>}

        {tab === 'devices' && (studentDevices.length === 0 ? <p className="py-6 text-center text-[13px] text-ink-500" data-id="element-280" data-mp="1xhyyct55yb3q">No covered devices yet.</p> : studentDevices.map(device => <div key={device.id} className="flex items-center gap-3 rounded-lg border border-ink-200 px-4 py-3" data-id="element-281" data-mp="q5gehktumh3l">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ink-100 text-ink-400" data-id="element-282" data-mp="v1wzi65r4fcg">
                  <LaptopIcon className="h-4 w-4" data-id="element-283" data-mp="13w32oj1zgolf" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-284" data-mp="13yfurp1ggfc7">
                  <span className="block truncate text-[13px] font-bold text-ink-900" data-id="element-285" data-mp="18w7numdu67hz">
                    {device.make} {device.model}
                  </span>
                  <span className="block text-[12.5px] text-ink-500" data-id="element-286" data-mp="1itm16d4gqg62">
                    {device.make} {device.model.split(' ')[0]} • {device.assetTagId}
                  </span>
                </span>
                <button type="button" onClick={() => setSheet('device')} className="h-8 shrink-0 rounded border border-ink-300 px-3 text-[12.5px] font-medium text-ink-700 hover:bg-ink-50" data-id="element-287" data-mp="3kkyt81lc0tkp">
                  Edit
                </button>
              </div>))}
      </div>
    </SidePanel>;
}